import axios from 'axios'
import { useAtomValue } from 'jotai'
import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'

import type { GaragePriceData, PwaGarageSingle } from '../types'
import { sanitizeHtml } from '../helpers'
import { searchSubmissionAtom } from '../../state'

export type SingleAvailabilityResponse = {
    id: string | number,
    available: boolean,
    time_range_valid: boolean,
    errors: string[],
}

export type SingleGaragePriceResponse = GaragePriceData | { errors: string[] }

export const useGetGarage = (
    garageId: string | number | undefined,
) => {
    const { i18n } = useTranslation()
    const hasGarageId = Boolean(garageId)

    const result = useQuery<PwaGarageSingle>({
        queryKey: [`garage-${garageId}`],
        queryFn: async () => {
            if (!hasGarageId) {
                throw new Error('Garage id is required.')
            }

            const { data } = await axios.get(`/api/pwa/garages/show?garage[id]=${garageId}`)
            return data
        },
        enabled: hasGarageId,
        select: data => {
            const htmlTagsAllowedForGarageNote = [
                // common html tags
                'span',
                'div',
                'p',
                'br',

                //
                // From admin editor:
                //
                'strong',
                'b',
                'u',
                'i',

                // lists
                'ul',
                'ol',
                'li',

                // links
                'a',

                'img',

                // indent
                'blockquote',
            ]

            const htmlTagsAllowedForGarageFaqAnswer = [
                'span',
                'div',
                'p',
                'br',
                'strong',
                'b',
                'u',
                'i',
                'ul',
                'ol',
                'li',
                'a',
                'blockquote',
            ]

            // sanitize data
            for (const translation of data.garage_translations) {
                translation.note = sanitizeHtml(translation.note, htmlTagsAllowedForGarageNote)
                translation.garage_faqs = translation.garage_faqs.map(faq => ({
                    ...faq,
                    question: sanitizeHtml(faq.question),
                    answer: sanitizeHtml(faq.answer, htmlTagsAllowedForGarageFaqAnswer),
                }))
            }
            return data
        },
    })

    return {
        result,
        getNotesTranslation: (
            data: PwaGarageSingle,
        ): PwaGarageSingle['garage_translations'][0] => {
            for (const lang of i18n.languages) {
                const trans = data.garage_translations.find(t => t.locale === lang)
                if (trans) {
                    return trans
                }
            }

            return {
                note: '',
                locale: '',
                name: '',
                popup_text: '',
                show_popup: false,
                garage_faqs: [],
            }
        },
    }
}

export const useGetGarageAvailability = (
    garageId: string | number | undefined,
) => {

    const searchSubmission = useAtomValue(searchSubmissionAtom)

    const {
        startDate,
        startTime,
        endDate,
        endTime,
    } = searchSubmission

    const result = useQuery<SingleAvailabilityResponse>({
        queryKey: [
            'garageAvailability',
            startDate?.format('YYYY-MM-DD'),
            startTime,
            endDate?.format('YYYY-MM-DD'),
            endTime,
            garageId,
        ],
        queryFn: async () => {
            const { data } = await axios.get(
                `/api/garages/single_availability?begins_at_date=${
                    startDate?.format('YYYY-MM-DD') ?? ''}&begins_at_time=${
                    startTime ?? ''}&ends_at_date=${
                    endDate?.format('YYYY-MM-DD') ?? ''}&ends_at_time=${
                    endTime ?? ''}&garage_id=${
                    garageId}`,
            )
            return data
        },
        enabled: !!garageId,
    })

    return result
}

export const useGetGaragePrice = (
    garageId: string | number | undefined,
) => {

    const searchSubmission = useAtomValue(searchSubmissionAtom)

    const {
        startDate,
        startTime,
        endDate,
        endTime,
    } = searchSubmission

    const result = useQuery<SingleGaragePriceResponse>({
        queryKey: [
            'garagePrice',
            garageId, 
            startDate?.format('YYYY-MM-DD'),
            startTime,
            endDate?.format('YYYY-MM-DD'),
            endTime
        ],
        queryFn: async () => {
            const { data } = await axios.get<SingleGaragePriceResponse>(
                `/api/garages/price?[price][begins_at_date]=${
                    startDate?.format('YYYY-MM-DD') ?? ''}&[price][begins_at_time]=${
                    startTime ?? ''}&[price][ends_at_date]=${
                    endDate?.format('YYYY-MM-DD') ?? ''}&[price][ends_at_time]=${
                    endTime ?? ''}&[price][garage_id]=${
                    garageId}`,
            )
            return data
        },
        enabled: !!garageId,
    })

    return result
}
